import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { message, Spin } from "antd";

import { useClient } from "../../context/ClientContext";
import { api } from "../../services/api";
import { config as appConfig } from "../../services/config";

const BACKEND = appConfig.URL_API.replace("/api", "");

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  surf: "#ffffff", bdr: "#e0e4f0", bdrF: "#2558d9",
  txt: "#0b0f1c", sub: "#5a6278", mut: "#9099b0",
  blue: "#2558d9", blueBg: "rgba(37,88,217,0.08)",
  ok: "#059669", okBg: "#d1fae5", danger: "#dc2626",
  rs: "7px", sh: "0 1px 3px rgba(0,0,0,0.06)",
};

const ICONS = ["⚡","💡","🔧","⭐","🏠","🛒","📦","🔌","🔦","💪","🌟","✅","🎯","🔐","🔩","🏗"];

const FONTS = [
  { value:"'Inter', system-ui, sans-serif",         label:"Inter" },
  { value:"'Montserrat', sans-serif",               label:"Montserrat" },
  { value:"'Lato', sans-serif",                     label:"Lato" },
  { value:"'Raleway', sans-serif",                  label:"Raleway" },
  { value:"'Poppins', sans-serif",                  label:"Poppins" },
  { value:"'Playfair Display', serif",              label:"Playfair Display" },
  { value:"'Merriweather', serif",                  label:"Merriweather" },
  { value:"Georgia, serif",                         label:"Georgia" },
  { value:"Arial, sans-serif",                      label:"Arial" },
  { value:"'Courier New', monospace",               label:"Courier New" },
];

// ─── Atoms ─────────────────────────────────────────────────────────────────────
function Toggle({ on, onChange }) {
  return (
    <button type="button" onClick={e => { e.stopPropagation(); onChange(!on); }}
      style={{ width:40,height:22,borderRadius:11,flexShrink:0,padding:0,border:"none",
        outline:"none",cursor:"pointer",background:on?T.blue:"#c8d0e0",position:"relative",transition:"background 0.2s" }}>
      <span style={{ display:"block",position:"absolute",top:2,left:on?20:2,width:18,height:18,
        borderRadius:"50%",background:"#fff",boxShadow:"0 1px 3px rgba(0,0,0,0.22)",
        transition:"left 0.18s cubic-bezier(.25,.1,.25,1)" }}/>
    </button>
  );
}

function TInput({ value, onChange, placeholder, type="text" }) {
  const [f, setF] = useState(false);
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} onFocus={() => setF(true)} onBlur={() => setF(false)}
      style={{ width:"100%",padding:"8px 11px",fontSize:13,color:T.txt,fontFamily:"inherit",
        border:`1.5px solid ${f?T.bdrF:T.bdr}`,borderRadius:T.rs,
        background:f?"#fff":"#fafbfe",outline:"none",
        boxShadow:f?`0 0 0 3px ${T.blueBg}`:"none",transition:"all 0.15s" }}/>
  );
}

function TArea({ value, onChange, placeholder, rows = 3 }) {
  const [f, setF] = useState(false);
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={rows} onFocus={() => setF(true)} onBlur={() => setF(false)}
      style={{ width:"100%",padding:"8px 11px",fontSize:13,color:T.txt,fontFamily:"inherit",
        lineHeight:1.65,resize:"vertical",
        border:`1.5px solid ${f?T.bdrF:T.bdr}`,borderRadius:T.rs,
        background:f?"#fff":"#fafbfe",outline:"none",
        boxShadow:f?`0 0 0 3px ${T.blueBg}`:"none",transition:"all 0.15s" }}/>
  );
}

// Input de WhatsApp con dos modos: Argentina (prefijo fijo +549, el usuario sólo
// carga el código de área + número) o internacional (número completo con código
// de país, para dueños de tienda con número de afuera, ej. +1 630-448-0120).
function WhatsAppNumberInput({ value, onChange, placeholder = "3400000000" }) {
  const [mode, setMode] = useState("ar");
  const [f, setF] = useState(false);
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || !value) return;
    hydrated.current = true;
    setMode(value.startsWith("549") ? "ar" : "intl");
  }, [value]);

  const raw = value || "";
  const arRest = raw.startsWith("549") ? raw.slice(3) : raw;

  return (
    <div>
      <div style={{ display:"flex",gap:6,marginBottom:8 }}>
        {[{ id:"ar", label:"🇦🇷 Argentina" }, { id:"intl", label:"🌎 Otro país" }].map(({ id, label }) => (
          <button key={id} type="button" onClick={() => setMode(id)}
            style={{ padding:"5px 10px",fontSize:11.5,fontWeight:600,borderRadius:6,cursor:"pointer",
              border:`1.5px solid ${mode===id?T.blue:T.bdr}`,
              background:mode===id?T.blueBg:"#fafbfe",color:mode===id?T.blue:T.sub,
              transition:"all 0.15s" }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display:"flex",alignItems:"stretch",border:`1.5px solid ${f?T.bdrF:T.bdr}`,
        borderRadius:T.rs,overflow:"hidden",background:f?"#fff":"#fafbfe",
        boxShadow:f?`0 0 0 3px ${T.blueBg}`:"none",transition:"all 0.15s" }}>
        <div style={{ display:"flex",alignItems:"center",gap:6,padding:"8px 11px",
          background:"#eef0f8",borderRight:`1.5px solid ${T.bdr}`,fontSize:13,
          color:T.sub,fontWeight:600,whiteSpace:"nowrap",flexShrink:0 }}>
          {mode==="ar" ? "🇦🇷 +549" : "🌎 +"}
        </div>
        {mode==="ar" ? (
          <input type="tel" value={arRest} placeholder={placeholder}
            onFocus={()=>setF(true)} onBlur={()=>setF(false)}
            onChange={e => onChange("549" + e.target.value.replace(/\D/g, ""))}
            style={{ flex:1,minWidth:0,padding:"8px 11px",fontSize:13,color:T.txt,
              fontFamily:"inherit",border:"none",outline:"none",background:"transparent" }}/>
        ) : (
          <input type="tel" value={raw} placeholder="16304480120"
            onFocus={()=>setF(true)} onBlur={()=>setF(false)}
            onChange={e => onChange(e.target.value.replace(/\D/g, ""))}
            style={{ flex:1,minWidth:0,padding:"8px 11px",fontSize:13,color:T.txt,
              fontFamily:"inherit",border:"none",outline:"none",background:"transparent" }}/>
        )}
      </div>
      <div style={{ fontSize:10.5,color:T.mut,marginTop:4 }}>
        {mode==="ar"
          ? "Se agrega automáticamente el +549."
          : "Ingresá el número completo con código de país (sin el +)."}
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return <div style={{ fontSize:9.5,fontWeight:700,color:T.mut,letterSpacing:.8,
    textTransform:"uppercase",marginBottom:5 }}>{children}</div>;
}

function Field({ label, children }) {
  return <div style={{ marginBottom:12 }}><FieldLabel>{label}</FieldLabel>{children}</div>;
}

function ColorChip({ label, value, onChange }) {
  return (
    <label title={label} style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:4,cursor:"pointer" }}>
      <div style={{ position:"relative",flexShrink:0 }}>
        <input type="color" value={value} onChange={e => onChange(e.target.value)}
          style={{ position:"absolute",inset:0,opacity:0,width:"100%",height:"100%",cursor:"pointer" }}/>
        <div style={{ width:26,height:26,borderRadius:7,background:value,
          border:"2px solid rgba(0,0,0,0.1)",boxShadow:"0 1px 4px rgba(0,0,0,0.14)" }}/>
      </div>
      <span style={{ fontSize:8.5,color:T.mut,whiteSpace:"nowrap" }}>{label}</span>
    </label>
  );
}

function TextStyleRow({ label, style, onChange, defaultColor }) {
  const s = style || {};
  return (
    <div style={{ display:"flex",alignItems:"center",gap:6,marginTop:4,padding:"4px 0",
      borderTop:`1px dashed ${T.bdr}` }}>
      {label && (
        <span style={{ fontSize:9,color:T.mut,textTransform:"uppercase",fontWeight:700,
          letterSpacing:.6,flexShrink:0,minWidth:32 }}>{label}</span>
      )}
      <select value={s.fontFamily||""} onChange={e => onChange({ ...s, fontFamily:e.target.value||undefined })}
        style={{ flex:1,padding:"3px 6px",fontSize:11,border:`1px solid ${T.bdr}`,borderRadius:5,
          color:T.txt,background:"#fafbfe",cursor:"pointer",fontFamily:s.fontFamily||"inherit" }}>
        <option value="">Sistema (por defecto)</option>
        {FONTS.map(f => (
          <option key={f.value} value={f.value} style={{ fontFamily:f.value }}>{f.label}</option>
        ))}
      </select>
      <input type="number" value={s.fontSize||""} placeholder="px" min={8} max={120}
        onChange={e => onChange({ ...s, fontSize:e.target.value ? Number(e.target.value) : undefined })}
        style={{ width:48,padding:"3px 5px",fontSize:11,border:`1px solid ${T.bdr}`,borderRadius:5,
          textAlign:"center",color:T.txt,background:"#fafbfe" }}/>
      <label style={{ cursor:"pointer",flexShrink:0,position:"relative" }}>
        <input type="color" value={s.color||(defaultColor||"#000000")}
          onChange={e => onChange({ ...s, color:e.target.value })}
          style={{ position:"absolute",opacity:0,width:"100%",height:"100%",cursor:"pointer" }}/>
        <div style={{ width:22,height:22,borderRadius:5,
          background:s.color||(defaultColor||"transparent"),
          border:s.color?"2px solid rgba(0,0,0,0.12)":"2px dashed #bbb",
          boxShadow:"0 1px 3px rgba(0,0,0,0.08)" }}/>
      </label>
      {s.color && (
        <button onClick={() => onChange({ ...s, color:undefined })}
          style={{ fontSize:11,color:T.mut,background:"none",border:"none",
            cursor:"pointer",padding:0,lineHeight:1,flexShrink:0 }}>×</button>
      )}
    </div>
  );
}

function VisibilityBar({ visible, onChange }) {
  return (
    <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"10px 14px",
      background:visible?T.okBg:"#f0f2f8",borderRadius:T.rs,
      border:`1.5px solid ${visible?"rgba(5,150,105,0.25)":T.bdr}` }}>
      <Toggle on={visible} onChange={onChange}/>
      <span style={{ fontSize:12.5,fontWeight:600,color:visible?T.ok:T.mut }}>
        {visible ? "Sección visible" : "Sección oculta en la landing"}
      </span>
    </div>
  );
}

// ─── Live Preview ───────────────────────────────────────────────────────────────
function LivePreview({ colors, hero, activeSlide, cards, quienes, contact, search, storeName, floatingWhatsapp, whatsappNumber }) {
  const grad = `linear-gradient(130deg, ${colors.primary} 0%, ${colors.accent} 100%)`;
  const slide = hero.slides?.[activeSlide] || {};
  const imageUrl = slide.image ? (slide.image.startsWith("http") || slide.image.startsWith("data:") ? slide.image : BACKEND + slide.image) : null;

  function HiddenOverlay() {
    return (
      <div style={{ position:"absolute",inset:0,
        background:"repeating-linear-gradient(45deg,rgba(0,0,0,0.03) 0,rgba(0,0,0,0.03) 1px,transparent 0,transparent 50%)",
        backgroundSize:"8px 8px",zIndex:1,display:"flex",alignItems:"center",justifyContent:"center" }}>
        <div style={{ background:"white",borderRadius:8,padding:"4px 12px",fontSize:11,
          color:T.mut,fontWeight:600,boxShadow:T.sh }}>Sección oculta</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily:"system-ui,sans-serif",fontSize:14,position:"relative" }}>
      {floatingWhatsapp?.enabled !== false && whatsappNumber && (
        <div style={{
          position:"absolute",bottom:14,right:14,width:38,height:38,borderRadius:"50%",
          background:"#25D366",display:"flex",alignItems:"center",justifyContent:"center",
          boxShadow:"0 3px 10px rgba(0,0,0,0.28)",zIndex:20,
        }}>
          <i className="bi bi-whatsapp" style={{ color:"#fff",fontSize:19 }}/>
        </div>
      )}
      {/* Navbar */}
      <div style={{ background:colors.primary,padding:"11px 22px",display:"flex",
        justifyContent:"space-between",alignItems:"center" }}>
        <span style={{ color:"white",fontWeight:700,fontSize:15 }}>{storeName || "Mi Tienda"}</span>
        <span style={{ color:"rgba(255,255,255,0.6)",fontSize:12 }}>Ver catálogo</span>
      </div>

      {/* Hero */}
      {hero.visible !== false && (
        <div style={{ position:"relative",minHeight:180,overflow:"hidden",
          background:imageUrl?"transparent":grad }}>
          {imageUrl && (
            <img src={imageUrl} alt="" style={{ position:"absolute",inset:0,width:"100%",
              height:"100%",objectFit:"cover" }}/>
          )}
          <div style={{ position:"absolute",inset:0,
            background:imageUrl?"rgba(0,0,0,0.38)":undefined }}/>
          <div style={{ position:"relative",zIndex:1,padding:"44px 24px 36px" }}>
            {slide.sub && (
              <div style={{ color:slide.subStyle?.color||"rgba(255,255,255,0.7)",
                fontSize:slide.subStyle?.fontSize||9.5,
                fontFamily:slide.subStyle?.fontFamily||"inherit",
                letterSpacing:3,marginBottom:10,fontWeight:600,textTransform:"uppercase" }}>{slide.sub}</div>
            )}
            {slide.title && (
              <div style={{ color:slide.titleStyle?.color||"white",
                fontSize:slide.titleStyle?.fontSize||30,
                fontFamily:slide.titleStyle?.fontFamily||"inherit",
                fontWeight:700,letterSpacing:-0.5,lineHeight:1.1,marginBottom:18 }}>{slide.title}</div>
            )}
            {slide.cta && (
              <div style={{ display:"inline-flex",background:"rgba(255,255,255,0.18)",
                border:"1.5px solid rgba(255,255,255,0.4)",borderRadius:9,padding:"9px 20px",
                color:slide.ctaStyle?.color||"white",
                fontSize:slide.ctaStyle?.fontSize||13,
                fontFamily:slide.ctaStyle?.fontFamily||"inherit",fontWeight:600 }}>
                {slide.cta}
              </div>
            )}
          </div>
          {/* Slide dots */}
          {(hero.slides?.length || 0) > 1 && (
            <div style={{ position:"absolute",bottom:12,left:"50%",transform:"translateX(-50%)",
              display:"flex",gap:5,zIndex:2 }}>
              {hero.slides.map((_, i) => (
                <div key={i} style={{ width:i===activeSlide?18:5,height:5,borderRadius:3,
                  background:i===activeSlide?"white":"rgba(255,255,255,0.4)",transition:"all 0.25s" }}/>
              ))}
            </div>
          )}
        </div>
      )}
      {hero.visible === false && (
        <div style={{ position:"relative",minHeight:60,background:colors.background }}>
          <HiddenOverlay/>
        </div>
      )}

      {/* Search */}
      {search?.enabled && (
        <div style={{ background:"#fff9f5",padding:"20px 18px" }}>
          {search.heading && (
            <div style={{ fontWeight:700,fontSize:13,textAlign:"center",marginBottom:10,color:colors.text }}>
              {search.heading}
            </div>
          )}
          <div style={{ border:`2px solid ${colors.primary}`,borderRadius:12,padding:"10px 12px",background:"#fff" }}>
            <div style={{ display:"flex",alignItems:"center",gap:8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.primary}
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <span style={{ fontSize:12,color:"#aaa",flex:1 }}>{search.title||"¿Qué producto estás buscando?"}</span>
            </div>
            {search.showCategoryChips !== false && (
              <div style={{ display:"flex",gap:6,marginTop:8,flexWrap:"wrap" }}>
                {["Categoría 1","Categoría 2","Categoría 3"].map(c => (
                  <span key={c} style={{ padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:600,
                    background:`${colors.primary}18`,color:colors.primary }}>{c}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cards */}
      {cards.items?.length > 0 && (
        <div style={{ position:"relative",background:colors.background,padding:"28px 22px" }}>
          {cards.visible === false && <HiddenOverlay/>}
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12,
            opacity:cards.visible===false?0.4:1 }}>
            {cards.items.map((card, i) => (
              <div key={i} style={{ background:"white",borderRadius:12,padding:"16px 14px",
                boxShadow:"0 1px 4px rgba(0,0,0,0.07)",border:`1.5px solid ${colors.bg2||"#e8edfa"}` }}>
                <div style={{ width:36,height:36,borderRadius:9,background:colors.bg2||"#e8edfa",
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,marginBottom:9 }}>
                  {card.icon}
                </div>
                <div style={{ fontWeight:700,
                  fontSize:cards.titleStyle?.fontSize||13,
                  fontFamily:cards.titleStyle?.fontFamily||"inherit",
                  color:cards.titleStyle?.color||colors.text,marginBottom:4 }}>{card.title}</div>
                <div style={{ fontSize:cards.descStyle?.fontSize||11.5,
                  fontFamily:cards.descStyle?.fontFamily||"inherit",
                  color:cards.descStyle?.color||colors.text,opacity:0.6,lineHeight:1.5 }}>{card.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quiénes Somos */}
      {quienes.title && (
        <div style={{ position:"relative",background:colors.bg2||"#e8edfa",padding:"28px 22px" }}>
          {quienes.visible === false && <HiddenOverlay/>}
          <div style={{ opacity:quienes.visible===false?0.4:1 }}>
            <div style={{ fontSize:quienes.titleStyle?.fontSize||20,
              fontFamily:quienes.titleStyle?.fontFamily||"inherit",
              fontWeight:700,color:quienes.titleStyle?.color||colors.text,marginBottom:6 }}>{quienes.title}</div>
            <div style={{ width:28,height:3,background:colors.primary,borderRadius:2,marginBottom:12 }}/>
            <div style={{ fontSize:quienes.bodyStyle?.fontSize||13,
              fontFamily:quienes.bodyStyle?.fontFamily||"inherit",
              lineHeight:1.7,color:quienes.bodyStyle?.color||colors.text,opacity:0.7 }}>{quienes.body}</div>
          </div>
        </div>
      )}

      {/* Contacto */}
      {(contact.address || contact.phone || contact.email) && (
        <div style={{ position:"relative",background:colors.background,padding:"28px 22px" }}>
          {contact.visible === false && <HiddenOverlay/>}
          <div style={{ opacity:contact.visible===false?0.4:1 }}>
            {contact.title && (
              <>
                <div style={{ fontSize:contact.titleStyle?.fontSize||20,
                  fontFamily:contact.titleStyle?.fontFamily||"inherit",
                  fontWeight:700,color:contact.titleStyle?.color||colors.text,marginBottom:6 }}>{contact.title}</div>
                <div style={{ width:28,height:3,background:colors.primary,borderRadius:2,marginBottom:12 }}/>
              </>
            )}
            <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
              {[["◎",contact.address],["☎",contact.phone],["✉",contact.email]]
                .filter(([,v]) => v)
                .map(([sym,val]) => (
                  <div key={sym} style={{ display:"flex",gap:9,
                    fontSize:contact.infoStyle?.fontSize||13,
                    fontFamily:contact.infoStyle?.fontFamily||"inherit",
                    color:contact.infoStyle?.color||colors.text,opacity:1 }}>
                    <span style={{ flexShrink:0 }}>{sym}</span><span>{val}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Hero Editor ───────────────────────────────────────────────────────────────
function HeroEditor({ data, setData, activeSlide, setActiveSlide }) {
  const { visible, slides = [] } = data;
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  function setVisible(v) { setData({ ...data, visible: v }); }
  function updSlide(i, key, val) {
    setData({ ...data, slides: slides.map((s, j) => j === i ? { ...s, [key]: val } : s) });
  }
  function addSlide() {
    if (slides.length >= 4) return;
    const newSlides = [...slides, { id: Date.now(), title:"", sub:"", cta:"", image:null }];
    setData({ ...data, slides: newSlides });
    setActiveSlide(slides.length);
  }
  function delSlide(i) {
    if (slides.length <= 1) return;
    const newSlides = slides.filter((_, j) => j !== i);
    setData({ ...data, slides: newSlides });
    setActiveSlide(v => Math.min(v, newSlides.length - 1));
  }
  async function handleImageFile(i, file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.tienda.uploadImagen(file);
      if (res.status === "ok") {
        updSlide(i, "image", res.url);
      } else {
        message.error(res.error || "Error al subir imagen");
      }
    } catch {
      message.error("Error de conexión al subir imagen");
    } finally {
      setUploading(false);
    }
  }

  const s = slides[activeSlide] || {};
  const imageUrl = s.image ? (s.image.startsWith("http") || s.image.startsWith("data:") ? s.image : BACKEND + s.image) : null;

  return (
    <div>
      <VisibilityBar visible={!!visible} onChange={setVisible}/>
      <div style={{ display:"flex",gap:20 }}>
        {/* Slide list */}
        <div style={{ width:150,flexShrink:0,display:"flex",flexDirection:"column",gap:6 }}>
          <FieldLabel>Slides del hero</FieldLabel>
          {slides.map((slide, i) => (
            <div key={slide.id||i} onClick={() => setActiveSlide(i)}
              style={{ border:`1.5px solid ${i===activeSlide?T.blue:T.bdr}`,borderRadius:T.rs,
                padding:"9px 10px",cursor:"pointer",
                background:i===activeSlide?T.blueBg:"#fafbfe",
                boxShadow:i===activeSlide?`0 0 0 3px ${T.blueBg}`:"none",
                position:"relative",transition:"all 0.15s" }}>
              <div style={{ height:28,borderRadius:5,overflow:"hidden",marginBottom:6,
                background:"linear-gradient(120deg,#4B72F5,#5BBEFC)" }}>
                {slide.image && (
                  <img src={slide.image.startsWith("http")||slide.image.startsWith("data:") ? slide.image : BACKEND+slide.image}
                    alt="" style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
                )}
              </div>
              <div style={{ fontSize:10.5,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",
                whiteSpace:"nowrap",color:i===activeSlide?T.blue:T.txt,lineHeight:1.2 }}>
                {slide.title||"Sin título"}
              </div>
              <div style={{ fontSize:9,color:T.mut,marginTop:1 }}>Slide {i+1}</div>
              {slides.length > 1 && (
                <button onClick={e => { e.stopPropagation(); delSlide(i); }}
                  style={{ position:"absolute",top:5,right:5,width:16,height:16,
                    background:"rgba(220,38,38,0.1)",color:T.danger,border:"none",
                    borderRadius:4,cursor:"pointer",fontSize:11,lineHeight:1,
                    display:"flex",alignItems:"center",justifyContent:"center",padding:0 }}>×</button>
              )}
            </div>
          ))}
          {slides.length < 4 && (
            <button onClick={addSlide}
              style={{ border:`1.5px dashed ${T.bdr}`,borderRadius:T.rs,padding:"9px 10px",
                background:"transparent",cursor:"pointer",color:T.mut,fontSize:11,
                fontWeight:500,textAlign:"center",transition:"all 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor="#aab0c0"}
              onMouseLeave={e => e.currentTarget.style.borderColor=T.bdr}>
              + Agregar slide
            </button>
          )}
        </div>

        {/* Slide fields */}
        <div style={{ flex:1 }}>
          <FieldLabel>Contenido — Slide {activeSlide+1}</FieldLabel>

          {/* Image upload */}
          <div style={{ marginBottom:12 }}>
            <FieldLabel>Imagen de fondo</FieldLabel>
            <div onClick={() => !uploading && fileInputRef.current?.click()}
              style={{ height:80,borderRadius:T.rs,border:`1.5px dashed ${T.bdr}`,
                background:imageUrl?"transparent":"#fafbfe",overflow:"hidden",
                cursor:uploading?"wait":"pointer",position:"relative",
                display:"flex",alignItems:"center",justifyContent:"center",transition:"border-color 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor=T.blue}
              onMouseLeave={e => e.currentTarget.style.borderColor=T.bdr}>
              {uploading ? (
                <div style={{ textAlign:"center" }}>
                  <Spin size="small"/>
                  <div style={{ fontSize:11,color:T.mut,marginTop:4 }}>Subiendo...</div>
                </div>
              ) : imageUrl ? (
                <>
                  <img src={imageUrl} alt="" style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
                  <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.35)",
                    display:"flex",alignItems:"center",justifyContent:"center",
                    opacity:0,transition:"opacity 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.opacity=1}
                    onMouseLeave={e => e.currentTarget.style.opacity=0}>
                    <span style={{ color:"white",fontSize:12,fontWeight:600 }}>Cambiar imagen</span>
                  </div>
                </>
              ) : (
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:20,marginBottom:4 }}>🖼</div>
                  <div style={{ fontSize:11,color:T.mut }}>Clic para subir imagen</div>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display:"none" }}
              onChange={e => handleImageFile(activeSlide, e.target.files[0])}/>
            {s.image && !uploading && (
              <button onClick={() => updSlide(activeSlide, "image", null)}
                style={{ marginTop:4,fontSize:11,color:T.danger,background:"none",
                  border:"none",cursor:"pointer",padding:0 }}>
                × Quitar imagen
              </button>
            )}
          </div>

          <div style={{ marginBottom:11 }}>
            <Field label="Título principal">
              <TInput value={s.title||""} onChange={v => updSlide(activeSlide,"title",v)} placeholder="Título grande del hero"/>
              <TextStyleRow label="Título" style={s.titleStyle} defaultColor="#ffffff"
                onChange={v => updSlide(activeSlide,"titleStyle",v)}/>
            </Field>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
            <Field label="Subtítulo">
              <TInput value={s.sub||""} onChange={v => updSlide(activeSlide,"sub",v)} placeholder="ELECTRICIDAD E ILUMINACIÓN"/>
              <TextStyleRow label="Sub" style={s.subStyle} defaultColor="rgba(255,255,255,0.7)"
                onChange={v => updSlide(activeSlide,"subStyle",v)}/>
            </Field>
            <Field label="Texto del botón CTA">
              <TInput value={s.cta||""} onChange={v => updSlide(activeSlide,"cta",v)} placeholder="Ver catálogo"/>
              <TextStyleRow label="CTA" style={s.ctaStyle} defaultColor="#ffffff"
                onChange={v => updSlide(activeSlide,"ctaStyle",v)}/>
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Cards Editor ──────────────────────────────────────────────────────────────
function CardsEditor({ data, setData }) {
  const { visible, items = [] } = data;
  const [activeCard, setActiveCard] = useState(0);

  function setVisible(v) { setData({ ...data, visible: v }); }
  function updCard(i, key, val) {
    setData({ ...data, items: items.map((c, j) => j === i ? { ...c, [key]: val } : c) });
  }
  function addCard() {
    if (items.length >= 6) return;
    const newItems = [...items, { id: Date.now(), icon:"⭐", title:"Nueva card", desc:"Descripción breve" }];
    setData({ ...data, items: newItems });
    setActiveCard(items.length);
  }
  function delCard(i) {
    if (items.length <= 1) return;
    const newItems = items.filter((_, j) => j !== i);
    setData({ ...data, items: newItems });
    setActiveCard(v => Math.min(v, newItems.length - 1));
  }

  const cIdx = Math.min(activeCard, items.length - 1);
  const c = items[cIdx] || {};

  return (
    <div>
      <VisibilityBar visible={!!visible} onChange={setVisible}/>
      <div style={{ display:"flex",gap:20 }}>
        <div style={{ width:150,flexShrink:0,display:"flex",flexDirection:"column",gap:6 }}>
          <FieldLabel>Cards ({items.length}/6)</FieldLabel>
          {items.map((card, i) => (
            <div key={card.id||i} onClick={() => setActiveCard(i)}
              style={{ border:`1.5px solid ${i===cIdx?T.blue:T.bdr}`,borderRadius:T.rs,
                padding:"8px 10px",cursor:"pointer",
                background:i===cIdx?T.blueBg:"#fafbfe",
                boxShadow:i===cIdx?`0 0 0 3px ${T.blueBg}`:"none",
                display:"flex",alignItems:"center",gap:8,position:"relative",transition:"all 0.15s" }}>
              <span style={{ fontSize:18,flexShrink:0 }}>{card.icon}</span>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontSize:10.5,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",
                  whiteSpace:"nowrap",color:i===cIdx?T.blue:T.txt }}>{card.title}</div>
              </div>
              {items.length > 1 && (
                <button onClick={e => { e.stopPropagation(); delCard(i); }}
                  style={{ position:"absolute",top:4,right:4,width:14,height:14,
                    background:"rgba(220,38,38,0.1)",color:T.danger,border:"none",
                    borderRadius:3,cursor:"pointer",fontSize:11,lineHeight:1,
                    display:"flex",alignItems:"center",justifyContent:"center",padding:0 }}>×</button>
              )}
            </div>
          ))}
          {items.length < 6 && (
            <button onClick={addCard}
              style={{ border:`1.5px dashed ${T.bdr}`,borderRadius:T.rs,padding:"8px 10px",
                background:"transparent",cursor:"pointer",color:T.mut,fontSize:11,
                fontWeight:500,textAlign:"center",transition:"all 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor="#aab0c0"}
              onMouseLeave={e => e.currentTarget.style.borderColor=T.bdr}>
              + Agregar card
            </button>
          )}
        </div>
        <div style={{ flex:1 }}>
          <FieldLabel>Editando card {cIdx+1}</FieldLabel>
          <div style={{ marginBottom:10 }}>
            <FieldLabel>Estilo de texto (todas las cards)</FieldLabel>
            <div style={{ display:"flex",flexDirection:"column",gap:2 }}>
              <TextStyleRow label="Título" style={data.titleStyle}
                onChange={v => setData({ ...data, titleStyle:v })}/>
              <TextStyleRow label="Desc" style={data.descStyle}
                onChange={v => setData({ ...data, descStyle:v })}/>
            </div>
          </div>
          <div style={{ marginBottom:13 }}>
            <FieldLabel>Ícono</FieldLabel>
            <div style={{ display:"flex",flexWrap:"wrap",gap:5 }}>
              {ICONS.map(icon => (
                <button key={icon} onClick={() => updCard(cIdx,"icon",icon)}
                  style={{ width:36,height:36,borderRadius:8,fontSize:18,
                    border:`1.5px solid ${c.icon===icon?T.blue:T.bdr}`,
                    background:c.icon===icon?T.blueBg:"#fafbfe",
                    cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                    boxShadow:c.icon===icon?`0 0 0 2.5px ${T.blueBg}`:"none",
                    transition:"all 0.1s",padding:0 }}>{icon}</button>
              ))}
            </div>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
            <Field label="Título">
              <TInput value={c.title||""} onChange={v => updCard(cIdx,"title",v)} placeholder="Título de la card"/>
            </Field>
            <Field label="Descripción">
              <TInput value={c.desc||""} onChange={v => updCard(cIdx,"desc",v)} placeholder="Descripción breve"/>
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Quiénes Editor ────────────────────────────────────────────────────────────
function QuienesEditor({ data, setData }) {
  return (
    <div>
      <VisibilityBar visible={!!data.visible} onChange={v => setData({ ...data, visible: v })}/>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 2fr",gap:10,alignItems:"flex-start" }}>
        <Field label="Título de sección">
          <TInput value={data.title||""} onChange={v => setData({ ...data, title:v })} placeholder="Quiénes Somos"/>
          <TextStyleRow label="Título" style={data.titleStyle}
            onChange={v => setData({ ...data, titleStyle:v })}/>
        </Field>
        <Field label="Texto descriptivo">
          <TArea value={data.body||""} onChange={v => setData({ ...data, body:v })} placeholder="Texto sobre la tienda..." rows={3}/>
          <TextStyleRow label="Cuerpo" style={data.bodyStyle}
            onChange={v => setData({ ...data, bodyStyle:v })}/>
        </Field>
      </div>
    </div>
  );
}

// ─── Contact Editor ────────────────────────────────────────────────────────────
function ContactEditor({ data, setData }) {
  return (
    <div>
      <VisibilityBar visible={!!data.visible} onChange={v => setData({ ...data, visible: v })}/>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10 }}>
        {[
          { key:"title",   label:"Título",    span:1, ph:"Encontranos" },
          { key:"address", label:"Dirección", span:2, ph:"Calle 123, Ciudad, Provincia" },
          { key:"phone",   label:"Teléfono",  span:1, ph:"(000) 000-0000" },
          { key:"email",   label:"Email",     span:2, ph:"email@tienda.com" },
        ].map(({ key, label, span, ph }) => (
          <div key={key} style={{ gridColumn:`span ${span}` }}>
            <Field label={label}>
              <TInput value={data[key]||""} onChange={v => setData({ ...data, [key]:v })} placeholder={ph}/>
            </Field>
          </div>
        ))}
        <div style={{ gridColumn:"span 2" }}>
          <FieldLabel>Estilo del título</FieldLabel>
          <TextStyleRow label="Título" style={data.titleStyle}
            onChange={v => setData({ ...data, titleStyle:v })}/>
        </div>
        <div style={{ gridColumn:"span 2" }}>
          <FieldLabel>Estilo info (dirección, teléfono, email)</FieldLabel>
          <TextStyleRow label="Info" style={data.infoStyle}
            onChange={v => setData({ ...data, infoStyle:v })}/>
        </div>
      </div>
    </div>
  );
}

// ─── Search Editor ─────────────────────────────────────────────────────────────
function SearchEditor({ data, setData }) {
  return (
    <div>
      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"10px 14px",
        background:data.enabled?T.okBg:"#f0f2f8",borderRadius:T.rs,
        border:`1.5px solid ${data.enabled?"rgba(5,150,105,0.25)":T.bdr}` }}>
        <Toggle on={!!data.enabled} onChange={v => setData({ ...data, enabled:v, visible:v })}/>
        <span style={{ fontSize:12.5,fontWeight:600,color:data.enabled?T.ok:T.mut }}>
          {data.enabled ? "Buscador activo en la landing" : "Buscador desactivado"}
        </span>
      </div>

      {data.enabled && (
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
          <Field label="Título del input (placeholder)">
            <TInput value={data.title||""} onChange={v => setData({ ...data, title:v })}
              placeholder="¿Qué producto estás buscando?"/>
          </Field>
          <Field label="Encabezado de sección (opcional)">
            <TInput value={data.heading||""} onChange={v => setData({ ...data, heading:v })}
              placeholder="Buscá lo que necesitás"/>
          </Field>
          <div style={{ gridColumn:"span 2",display:"flex",alignItems:"center",gap:10,
            padding:"10px 14px",background:"#f8f9ff",borderRadius:T.rs,border:`1px solid ${T.bdr}` }}>
            <Toggle on={data.showCategoryChips!==false} onChange={v => setData({ ...data, showCategoryChips:v })}/>
            <span style={{ fontSize:12.5,color:T.sub }}>Mostrar chips de categorías debajo del input</span>
          </div>
        </div>
      )}

      {!data.enabled && (
        <div style={{ padding:"20px",textAlign:"center",color:T.mut,fontSize:13,
          background:"#fafbfe",borderRadius:T.rs,border:`1px dashed ${T.bdr}` }}>
          Activá el buscador para configurar sus opciones. Permite que los visitantes busquen productos o categorías desde la página de inicio.
        </div>
      )}
    </div>
  );
}

// ─── WhatsApp Editor ───────────────────────────────────────────────────────────
function WhatsAppEditor({ data, setData, whatsappNumber, onChangeNumber }) {
  const enabled = data.enabled !== false;
  return (
    <div>
      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"10px 14px",
        background:enabled?T.okBg:"#f0f2f8",borderRadius:T.rs,
        border:`1.5px solid ${enabled?"rgba(5,150,105,0.25)":T.bdr}` }}>
        <Toggle on={enabled} onChange={v => setData({ ...data, enabled:v })}/>
        <span style={{ fontSize:12.5,fontWeight:600,color:enabled?T.ok:T.mut }}>
          {enabled ? "Botón flotante de WhatsApp activo" : "Botón flotante desactivado"}
        </span>
      </div>
      <p style={{ fontSize:12.5,color:T.sub,lineHeight:1.6,marginBottom:14 }}>
        Cuando está activo, aparece un botón flotante abajo a la derecha en toda la tienda para que los visitantes te escriban por WhatsApp con un solo click.
      </p>
      <Field label="Número de WhatsApp">
        <WhatsAppNumberInput value={whatsappNumber} onChange={onChangeNumber}/>
      </Field>
      {!whatsappNumber && (
        <div style={{ marginTop:10,padding:"10px 14px",background:"#fff7ed",borderRadius:T.rs,
          border:"1px solid rgba(234,88,12,0.25)",fontSize:12,color:"#c2410c",lineHeight:1.5 }}>
          Cargá tu número para que el botón se muestre en la tienda.
        </div>
      )}
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────────
export const ConfigLanding = () => {
  const realCtx = useClient();
  const { config, refreshConfig, loadingConfig } = realCtx;

  const [active,    setActive]    = useState("hero");
  const [device,    setDevice]    = useState("desktop");
  const [saveState, setSaveState] = useState("idle");
  const [activeSlide, setActiveSlide] = useState(0);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const [colors, setColors] = useState({
    primary: "#4B72F5", accent: "#5BBEFC",
    background: "#f0f3ff", bg2: "#e8edfa",
    text: "#1a1a3a", primary_text: "#ffffff",
  });
  const [hero,    setHero]    = useState({ visible:true, slides:[{id:1,title:"",sub:"",cta:"",image:null}] });
  const [cards,   setCards]   = useState({ visible:true, items:[{id:1,icon:"⚡",title:"",desc:""}] });
  const [quienes, setQuienes] = useState({ visible:true, title:"Quiénes Somos", body:"" });
  const [contact, setContact] = useState({ visible:true, title:"Encontranos", address:"", phone:"", email:"" });
  const [search,  setSearch]  = useState({ visible:true, enabled:false, title:"¿Qué producto estás buscando?", heading:"", showCategoryChips:true });
  const [floatingWhatsapp, setFloatingWhatsapp] = useState({ enabled:true });
  const [whatsappNumber, setWhatsappNumber] = useState("");

  const syncedRef = useRef(false);
  useEffect(() => {
    if (loadingConfig || syncedRef.current || !config?.id) return;
    syncedRef.current = true;

    setColors({
      primary:      config.theme?.primaryColor      || "#4B72F5",
      accent:       config.theme?.accentColor       || "#5BBEFC",
      background:   config.theme?.backgroundColor   || "#f0f3ff",
      bg2:          config.theme?.secondaryColor     || "#e8edfa",
      text:         config.theme?.textColor         || "#1a1a3a",
      primary_text: config.theme?.primaryTextColor  || "#ffffff",
    });
    if (config.uiConfig?.hero) {
      // Strip legacy base64 images — only keep URL-based images
      const cleanHero = {
        ...config.uiConfig.hero,
        slides: (config.uiConfig.hero.slides || []).map(s => ({
          ...s,
          image: s.image && s.image.startsWith("/uploads/") ? s.image : null,
        })),
      };
      setHero(cleanHero);
    }
    if (config.uiConfig?.cards)   setCards(config.uiConfig.cards);
    if (config.uiConfig?.quienes) setQuienes(config.uiConfig.quienes);
    if (config.uiConfig?.contact) setContact(config.uiConfig.contact);
    if (config.uiConfig?.search)  setSearch(config.uiConfig.search);
    if (config.uiConfig?.floatingWhatsapp) setFloatingWhatsapp(config.uiConfig.floatingWhatsapp);
    setWhatsappNumber(config.contact?.whatsapp || "");
  }, [loadingConfig, config]);

  const setColor = useCallback((key, val) => setColors(p => ({ ...p, [key]: val })), []);

  const previewColors = useMemo(() => ({
    primary: colors.primary, accent: colors.accent,
    background: colors.background, bg2: colors.bg2,
    text: colors.text,
  }), [colors]);

  const handleSave = async () => {
    if (saveState !== "idle") return;
    setSaveState("saving");
    try {
      // Strip any base64 images before saving — only keep URL-based images (/uploads/...)
      const safeHero = {
        ...hero,
        slides: hero.slides.map(s => ({
          ...s,
          image: s.image && s.image.startsWith("/uploads/") ? s.image : null,
        })),
      };
      const uiConfig = { ...(config?.uiConfig||{}), hero: safeHero, cards, quienes, contact, search, floatingWhatsapp };
      const res = await api.tienda.updateConfig({
        color_primary:      colors.primary,
        color_secondary:    colors.bg2,
        color_accent:       colors.accent,
        color_background:   colors.background,
        color_text:         colors.text,
        color_primary_text: colors.primary_text,
        ui_config:          JSON.stringify(uiConfig),
        whatsapp:           whatsappNumber,
      });
      if (res.status === "ok") {
        await refreshConfig();
        setSaveState("saved");
        setTimeout(() => setSaveState("idle"), 1800);
      } else {
        message.error(res.error || "Error al guardar");
        setSaveState("idle");
      }
    } catch {
      message.error("Error de conexión");
      setSaveState("idle");
    }
  };

  const tabs = [
    { id:"hero",    label:"Hero",          dot: hero.visible },
    { id:"cards",   label:"Destacados",    dot: cards.visible },
    { id:"quienes", label:"Quiénes Somos", dot: quienes.visible },
    { id:"contact", label:"Encontranos",   dot: contact.visible },
    { id:"search",  label:"Buscador",      dot: search.visible && search.enabled },
    { id:"whatsapp", label:"WhatsApp",     dot: floatingWhatsapp.enabled },
  ];

  if (loadingConfig) {
    return (
      <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:"60vh" }}>
        <Spin size="large" tip="Cargando configuración..."/>
      </div>
    );
  }

  return (
    <div style={{ margin: isMobile ? -16 : -24, height:"calc(100vh - 56px)",display:"flex",
      flexDirection:"column",overflow:"hidden",
      fontFamily:"system-ui,sans-serif",background:"#eaecf4" }}>

      {/* ── Toolbar ── */}
      <div style={{ flexShrink:0,background:T.surf,borderBottom:`1px solid ${T.bdr}`,
        overflowX:"auto" }}>
        <div style={{ minWidth:620,height:56,padding:"0 20px",display:"flex",alignItems:"center",gap:14 }}>
        <div style={{ fontWeight:700,fontSize:14,color:T.txt,whiteSpace:"nowrap" }}>Personalización</div>
        <div style={{ width:1,height:20,background:T.bdr,flexShrink:0 }}/>

        <div style={{ display:"flex",alignItems:"flex-end",gap:11,flexShrink:0 }}>
          <span style={{ fontSize:10,fontWeight:600,color:T.mut,letterSpacing:.4,
            textTransform:"uppercase",marginBottom:3,whiteSpace:"nowrap" }}>Colores</span>
          {[
            { k:"primary",      l:"Principal" },
            { k:"accent",       l:"Acento" },
            { k:"background",   l:"Fondo 1" },
            { k:"bg2",          l:"Fondo 2" },
            { k:"text",         l:"Texto" },
            { k:"primary_text", l:"T. sobre P." },
          ].map(({ k, l }) => (
            <ColorChip key={k} label={l} value={colors[k]} onChange={v => setColor(k, v)}/>
          ))}
        </div>

        <div style={{ flex:1 }}/>

        <div style={{ display:"flex",alignItems:"center",gap:6,flexShrink:0 }}>
          <div style={{ width:7,height:7,borderRadius:"50%",background:"#22c55e",
            animation:"pulse-ring 2.5s ease-in-out infinite" }}/>
          <span style={{ fontSize:11,color:T.mut }}>En vivo</span>
        </div>

        <div style={{ display:"flex",gap:4,flexShrink:0 }}>
          {[{id:"desktop",label:"🖥"},{id:"mobile",label:"📱"}].map(({ id, label }) => (
            <button key={id} onClick={() => setDevice(id)}
              style={{ width:30,height:30,borderRadius:7,fontSize:13,cursor:"pointer",
                border:`1.5px solid ${device===id?T.blue:T.bdr}`,
                background:device===id?T.blueBg:"transparent",
                display:"flex",alignItems:"center",justifyContent:"center",outline:"none",
                transition:"all 0.15s" }}>{label}</button>
          ))}
        </div>

        <button onClick={handleSave}
          style={{ padding:"8px 20px",borderRadius:9,border:"none",
            background:saveState==="saved"?"#059669":T.blue,color:"white",
            fontSize:13,fontWeight:600,cursor:"pointer",
            transition:"background 0.25s",boxShadow:"0 2px 10px rgba(37,88,217,0.2)",
            display:"flex",alignItems:"center",gap:7 }}>
          {saveState==="saving" && (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"
              style={{ animation:"spin 0.7s linear infinite" }}>
              <circle cx="6.5" cy="6.5" r="5" stroke="rgba(255,255,255,0.3)" strokeWidth="2"/>
              <path d="M6.5 1.5 A5 5 0 0 1 11.5 6.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
          {saveState==="saved" ? "✓ Guardado" : "Guardar"}
        </button>
        </div>
      </div>

      {/* ── Preview area ── */}
      <div style={{ flex:1,overflow:"auto",minHeight:0,display:"flex",justifyContent:"center",
        padding:"22px 20px",
        backgroundImage:"radial-gradient(#bfc6d6 1px,transparent 1px)",
        backgroundSize:"20px 20px" }}>
        <div style={{ width:device==="desktop"?"100%":"390px",maxWidth:860,flexShrink:0,
          minWidth: isMobile ? 320 : undefined,
          transition:"width 0.3s cubic-bezier(.25,.1,.25,1)" }}>
          {/* Browser chrome */}
          <div style={{ background:"#1e2230",borderRadius:"10px 10px 0 0",
            padding:"10px 14px",display:"flex",alignItems:"center",gap:10 }}>
            <div style={{ display:"flex",gap:5.5 }}>
              {["#ff5f57","#febc2e","#28c840"].map(c => (
                <div key={c} style={{ width:10,height:10,borderRadius:"50%",background:c }}/>
              ))}
            </div>
            <div style={{ flex:1,background:"#2e3347",borderRadius:5,padding:"4px 10px",
              fontSize:10.5,color:"#6b7a8f",textAlign:"center",
              display:"flex",alignItems:"center",justifyContent:"center",gap:5 }}>
              <span style={{ opacity:.6 }}>🔒</span>
              {config?.id ? `${config.id}.ip-web.com.ar` : "tutienda.com.ar"}
            </div>
            <div style={{ width:16 }}/>
          </div>
          <div style={{ borderRadius:"0 0 10px 10px",overflow:"hidden",
            border:`1px solid ${T.bdr}`,borderTop:"none",
            boxShadow:"0 6px 24px rgba(0,0,0,0.09)" }}>
            <LivePreview
              colors={previewColors}
              hero={hero}
              activeSlide={activeSlide}
              cards={cards}
              quienes={quienes}
              contact={contact}
              search={search}
              storeName={config?.displayName || config?.name}
              floatingWhatsapp={floatingWhatsapp}
              whatsappNumber={whatsappNumber}
            />
          </div>
        </div>
      </div>

      {/* ── Bottom drawer ── */}
      <div style={{ background:T.surf,borderTop:`2px solid ${T.bdr}`,
        flexShrink:0,display:"flex",flexDirection:"column",height:270 }}>
        <div style={{ display:"flex",borderBottom:`1px solid ${T.bdr}`,
          padding:"0 18px",overflowX:"auto",flexShrink:0 }}>
          {tabs.map(({ id, label, dot }) => {
            const a = active === id;
            return (
              <button key={id} onClick={() => setActive(id)}
                style={{ display:"flex",alignItems:"center",gap:6,
                  padding:"10px 15px",border:"none",background:"transparent",cursor:"pointer",
                  fontSize:13,fontWeight:a?600:400,color:a?T.blue:T.sub,
                  borderBottom:`2.5px solid ${a?T.blue:"transparent"}`,
                  marginBottom:-1,flexShrink:0,outline:"none",transition:"color 0.15s" }}>
                <div style={{ width:6,height:6,borderRadius:"50%",flexShrink:0,
                  background:dot?"#22c55e":"#c8d0e0",transition:"background 0.2s" }}/>
                {label}
              </button>
            );
          })}
          <div style={{ marginLeft:"auto",display:"flex",alignItems:"center",paddingRight:4 }}>
            <span style={{ fontSize:10.5,color:T.mut }}>Cambiá de sección con las pestañas</span>
          </div>
        </div>
        <div style={{ flex:1,overflowY:"auto",padding:"16px 18px" }}>
          {active==="hero"    && <HeroEditor    data={hero}    setData={setHero}
                                  activeSlide={activeSlide} setActiveSlide={setActiveSlide}/>}
          {active==="cards"   && <CardsEditor   data={cards}   setData={setCards}/>}
          {active==="quienes" && <QuienesEditor data={quienes} setData={setQuienes}/>}
          {active==="contact" && <ContactEditor data={contact} setData={setContact}/>}
          {active==="search"  && <SearchEditor  data={search}  setData={setSearch}/>}
          {active==="whatsapp" && <WhatsAppEditor data={floatingWhatsapp} setData={setFloatingWhatsapp} whatsappNumber={whatsappNumber} onChangeNumber={setWhatsappNumber}/>}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Montserrat:wght@400;600;700&family=Lato:wght@400;700&family=Raleway:wght@400;600;700&family=Poppins:wght@400;600;700&family=Playfair+Display:wght@400;700&family=Merriweather:wght@400;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-ring { 0%,100%{opacity:1} 50%{opacity:.45} }
      `}</style>
    </div>
  );
};
